import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(40px); }
  to { opacity: 1; transform: translateY(0); }
`;

/* ═══════════════════════════════════════════
   PAGE HERO
   ═══════════════════════════════════════════ */

export const PageHero = styled.section<{ $bgImage?: string }>`
  min-height: 100vh;
  background-image:
    linear-gradient(180deg,
      rgba(10, 22, 40, 0.1) 0%,
      rgba(10, 22, 40, 0.35) 50%,
      rgba(10, 22, 40, 0.92) 85%,
      var(--bg-primary) 100%),
    url(${({ $bgImage }) => $bgImage || ''});
  background-size: cover;
  background-position: center 15%;
  background-attachment: fixed;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 4rem 1.5rem;

  @media (max-width: 768px) {
    background-attachment: scroll;
    min-height: 85vh;
    background-position: center 20%;
  }
`;

export const HeroInner = styled.div`
  max-width: 900px;
  margin: 0 auto;
  width: 100%;
  animation: ${fadeInUp} 0.8s ease-out;
`;

export const HeroEyebrow = styled.div`
  display: inline-block;
  font-family: var(--font-heading);
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: var(--accent-teal);
  background: rgba(10, 22, 40, 0.55);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  border: 1px solid rgba(0, 201, 177, 0.3);
  padding: 0.5rem 1.4rem;
  border-radius: 100px;
  margin-bottom: 1rem;
`;

export const HeroTitle = styled.h1`
  font-family: var(--font-heading);
  font-size: clamp(3rem, 6vw, 5rem);
  font-weight: 800;
  color: var(--text-primary);
  margin-bottom: 1.2rem;
  text-shadow: 0 4px 30px rgba(0, 0, 0, 0.5);

  span {
    background: linear-gradient(135deg, #87CEEB 0%, #2196F3 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

export const HeroSubtitle = styled.p`
  font-size: clamp(1rem, 1.8vw, 1.2rem);
  color: var(--text-primary);
  max-width: 640px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.8;
  background: rgba(10, 22, 40, 0.5);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  padding: 1rem 1.5rem;
  border-radius: var(--border-radius-lg);
`;

/* ═══════════════════════════════════════════
   SHARED
   ═══════════════════════════════════════════ */

export const SectionEyebrow = styled.div`
  font-family: var(--font-heading);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: var(--accent-teal);
  margin-bottom: 0.75rem;
`;

export const SectionTitle = styled.h2`
  font-family: var(--font-heading);
  font-size: clamp(2rem, 4vw, 2.8rem);
  font-weight: 800;
  color: var(--text-primary);
  margin-bottom: 1.5rem;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 50px;
    height: 3px;
    background: var(--gradient-accent);
    border-radius: 2px;
  }
`;

export const BodyText = styled.p`
  font-size: 1.05rem;
  line-height: 1.8;
  color: var(--text-secondary);
  margin-bottom: 1.5rem;
`;

/* ═══════════════════════════════════════════
   STORY SECTION (founder / origin)
   ═══════════════════════════════════════════ */

export const StorySection = styled.section`
  padding: 6rem 0;
  background: var(--bg-primary);
`;

export const StoryGrid = styled.div`
  max-width: var(--container-max);
  margin: 0 auto;
  padding: 0 1.5rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

export const StoryImage = styled.div`
  position: relative;

  img {
    border-radius: var(--border-radius-lg);
    width: 100%;
    box-shadow: var(--shadow-lg);
  }

  &::before {
    content: '';
    position: absolute;
    top: 12px;
    left: 12px;
    right: -12px;
    bottom: -12px;
    border: 2px solid var(--accent-teal);
    border-radius: var(--border-radius-lg);
    opacity: 0.15;
    z-index: -1;
  }
`;

export const StoryContent = styled.div``;

/* ═══════════════════════════════════════════
   VALUES SECTION
   ═══════════════════════════════════════════ */

export const ValuesSection = styled.section`
  padding: 6rem 0;
  background: var(--bg-secondary);
`;

export const ValuesContainer = styled.div`
  max-width: var(--container-max);
  margin: 0 auto;
  padding: 0 1.5rem;
`;

export const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  margin-top: 2rem;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

export const ValueCard = styled.div`
  background: var(--gradient-card);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  padding: 2rem;
  transition: all var(--transition-base);

  &:hover {
    transform: translateY(-4px);
    border-color: var(--border-hover);
    box-shadow: var(--shadow-glow-teal);
  }
`;

export const ValueIcon = styled.div`
  color: var(--accent-gold);
  margin-bottom: 1rem;
`;

export const ValueTitle = styled.h3`
  font-family: var(--font-heading);
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
`;

export const ValueText = styled.p`
  font-size: 0.92rem;
  line-height: 1.7;
  color: var(--text-muted);
  margin: 0;
`;

/* ═══════════════════════════════════════════
   BOAT SECTION
   ═══════════════════════════════════════════ */

export const BoatSection = styled.section`
  padding: 6rem 0;
  background: var(--bg-primary);
`;

export const BoatGrid = styled.div`
  max-width: var(--container-max);
  margin: 0 auto;
  padding: 0 1.5rem;
  display: grid;
  grid-template-columns: 1fr 1.3fr;
  gap: 3rem;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

export const BoatImage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    border-radius: var(--border-radius-lg);
    width: 100%;
    max-width: 640px;
    box-shadow: var(--shadow-lg);
  }

  @media (max-width: 768px) {
    order: -1;
  }
`;

export const BoatContent = styled.div``;

export const SpecsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-top: 1.5rem;
  margin-bottom: 2rem;
`;

export const SpecItem = styled.div`
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  padding: 1rem;
  text-align: center;
`;

export const SpecValue = styled.div`
  font-family: var(--font-heading);
  font-size: 1.3rem;
  font-weight: 800;
  color: var(--accent-teal);
  margin-bottom: 0.2rem;
`;

export const SpecLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: var(--text-muted);
`;

export const GearList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1.5rem 0;
`;

export const GearItem = styled.li`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6rem 0;
  border-bottom: 1px solid var(--border-color);
  font-size: 0.95rem;
  color: var(--text-secondary);

  &:last-child {
    border-bottom: none;
  }

  svg {
    color: var(--accent-teal);
    flex-shrink: 0;
  }
`;

/* ═══════════════════════════════════════════
   FAQ SECTION
   ═══════════════════════════════════════════ */

export const FaqSection = styled.section`
  padding: 6rem 0;
  background: var(--bg-secondary);
`;

export const FaqContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

export const FaqItem = styled.div`
  border-bottom: 1px solid var(--border-color);
  padding: 1.5rem 0;

  &:first-of-type {
    border-top: 1px solid var(--border-color);
  }
`;

export const FaqQuestion = styled.button`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  background: none;
  border: none;
  font-family: var(--font-heading);
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--text-primary);
  text-align: left;
  cursor: pointer;
  padding: 0;
  transition: color var(--transition-fast);

  &:hover {
    color: var(--accent-teal);
  }

  svg {
    color: var(--accent-teal);
    flex-shrink: 0;
    margin-left: 1rem;
    transition: transform var(--transition-fast);
  }
`;

export const FaqAnswer = styled.div<{ $open: boolean }>`
  max-height: ${({ $open }) => ($open ? '500px' : '0')};
  overflow: hidden;
  transition: max-height 0.4s ease;
  padding-top: ${({ $open }) => ($open ? '1rem' : '0')};

  p {
    font-size: 0.95rem;
    line-height: 1.7;
    color: var(--text-secondary);
    margin: 0;
  }
`;

/* ═══════════════════════════════════════════
   CTA SECTION
   ═══════════════════════════════════════════ */

export const CtaSection = styled.section<{ $bgImage?: string }>`
  padding: 5rem 0;
  background-image:
    linear-gradient(135deg, rgba(10, 22, 40, 0.88) 0%, rgba(15, 31, 53, 0.82) 100%),
    url(${({ $bgImage }) => $bgImage || ''});
  background-size: cover;
  background-position: center 30%;
  background-attachment: fixed;
  text-align: center;
  position: relative;

  @media (max-width: 768px) {
    background-attachment: scroll;
    background-position: center 20%;
  }

  &::before, &::after {
    content: '';
    position: absolute;
    left: 0; right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, var(--accent-gold), transparent);
    opacity: 0.25;
  }
  &::before { top: 0; }
  &::after { bottom: 0; }
`;

export const CtaContainer = styled.div`
  max-width: 700px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

export const CtaButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--gradient-gold);
  color: var(--text-inverse);
  padding: 1rem 2.5rem;
  border-radius: var(--border-radius);
  font-family: var(--font-heading);
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  transition: all var(--transition-base);
  box-shadow: var(--shadow-glow-gold);
  margin-top: 1.5rem;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 30px rgba(244, 170, 0, 0.4);
    color: var(--text-inverse);
  }
`;
